import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { getNotificationDetail } from "../api/notification.api";
import type { NotificationDetailResponse } from "../types/notification.types";

interface UseGetNotificationDetailReturn {
  notification: NotificationDetailResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<NotificationDetailResponse | null>;
}

export const useGetNotificationDetail = (notificationId?: string): UseGetNotificationDetailReturn => {
  const [notification, setNotification] = useState<NotificationDetailResponse | null>(null);
  const [requestedNotificationId, setRequestedNotificationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, cancel, loading } = useLatestRequest();
  const { handleError } = useResultHandler();

  const fetchNotificationDetail = useCallback(async (): Promise<NotificationDetailResponse | null> => {
    if (!notificationId) return null;

    setRequestedNotificationId(notificationId);
    setNotification(null);
    setError(null);

    try {
      const response = await runLatestRequest((signal) => getNotificationDetail(notificationId, signal));
      if (!response) return null;

      setNotification(response);
      return response;
    } catch (requestError: unknown) {
      setError(handleError(requestError, "Failed to fetch notification"));
      return null;
    }
  }, [handleError, notificationId, runLatestRequest]);

  useEffect(() => {
    if (!notificationId) {
      cancel();
      setRequestedNotificationId(null);
      setNotification(null);
      setError(null);
      return;
    }

    void fetchNotificationDetail();
  }, [cancel, fetchNotificationDetail, notificationId]);

  const isCurrentNotification = requestedNotificationId === notificationId;

  return {
    notification: isCurrentNotification ? notification : null,
    isLoading: Boolean(notificationId) && (!isCurrentNotification || loading),
    error: isCurrentNotification ? error : null,
    refetch: fetchNotificationDetail,
  };
};
